// @ts-check

/**
 * @file Hosts the shared confirm modal UI.
 */

import _ from "gettext-universal/build/src/translate.js"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {Pressable, Text, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import Modal from "./modal"
import {registerConfirmModalHost} from "../shared/confirm-modal"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {ConfirmModalState} from "../shared/confirm-modal" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} ConfirmModalHostProps */

/**
 * @typedef {object} ConfirmModalHostState
 * @property {ConfirmModalState | null} state Active confirm modal state.
 */

/**
 * Renders a centered confirm modal driven by the shared `confirmModal()` helper.
 * @augments {ShapeComponent<ConfirmModalHostProps, ConfirmModalHostState>}
 */
class ConfirmModalHost extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {ConfirmModalHostState} */
  state = {
    state: null
  }

  /** Registers this component as the shared confirm modal host. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    useEffect(() => {
      registerConfirmModalHost(this.onHostStateChange)
      return () => {
        registerConfirmModalHost(null)
      }
    }, [])
  }

  /**
   * Renders the active confirm modal.
   * @returns {ReactNode} Confirm modal content.
   */
  render() {
    const modalState = this.s.state
    const isDestructive = modalState?.options.tone === "destructive"
    const confirmLabel = modalState?.options.confirmLabel || _("Confirm")
    const cancelLabel = modalState?.options.cancelLabel || _("Cancel")
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const title =
      modalState?.options.title ||
      (isDestructive ? _("Confirm delete") : _("Confirm"))
    if (!modalState) return null

    return (
      <Modal
        backdropTestID="confirmModalHost/backdropPress"
        cardStyle={styles.dialog ||= {
          borderRadius: 14,
          maxWidth: 420,
          paddingHorizontal: 20,
          paddingVertical: 18,
          shadowColor: "#0f172a",
          shadowOffset: {
            height: 12,
            width: 0
          },
          shadowOpacity: 0.18,
          shadowRadius: 32,
          zIndex: 200
        }}
        cardTestID="confirmModalHost/dialog"
        isVisible
        onRequestClose={this.tt.onCancelPress}
        overlayStyle={styles.backdropOverlay ||= {
          zIndex: 200
        }}
        testID="confirmModalHost/backdrop"
      >
        <Text
          style={styles[`dialogTitle-${themeName}-${isDestructive}`] ||= {
            color: isDestructive ? colors.dangerText : colors.text,
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8
          }}
          testID="confirmModalHost/title"
        >
          {title}
        </Text>
        <Text
          style={styles[`dialogMessage-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 16
          }}
          testID="confirmModalHost/message"
        >
          {modalState?.message || ""}
        </Text>
        <View
          style={styles.dialogActions ||= {
            flexDirection: "row",
            gap: 8,
            justifyContent: "flex-end"
          }}
          testID="confirmModalHost/actions"
        >
          <Pressable
            onPress={this.tt.onCancelPress}
            style={styles[`cancelButton-${themeName}`] ||= {
              alignItems: "center",
              backgroundColor: colors.surfaceMuted,
              borderRadius: 8,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 8
            }}
            testID="confirmModalHost/cancelButton"
          >
            <View
              style={styles.cancelButtonIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID="confirmModalHost/cancelButton/icon"
            >
              <FontAwesome6 color={colors.text} name="xmark" size={14} />
            </View>
            <Text
              style={styles[`cancelButtonText-${themeName}`] ||= {
                color: colors.text,
                fontSize: 14,
                fontWeight: 700
              }}
              testID="confirmModalHost/cancelButton/label"
            >
              {cancelLabel}
            </Text>
          </Pressable>
          <Pressable
            onPress={this.tt.onConfirmPress}
            style={styles[`confirmButton-${themeName}-${isDestructive}`] ||= {
              alignItems: "center",
              backgroundColor: isDestructive ? colors.danger : colors.primary,
              borderRadius: 8,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 8
            }}
            testID="confirmModalHost/confirmButton"
          >
            <View
              style={styles.confirmButtonIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID="confirmModalHost/confirmButton/icon"
            >
              <FontAwesome6 color={colors.onPrimary} name={isDestructive ? "trash" : "check"} size={14} />
            </View>
            <Text
              style={styles[`confirmButtonText-${themeName}`] ||= {
                color: colors.onPrimary,
                fontSize: 14,
                fontWeight: 700
              }}
              testID="confirmModalHost/confirmButton/label"
            >
              {confirmLabel}
            </Text>
          </Pressable>
        </View>
      </Modal>
    )
  }

  /**
   * Stores the active shared confirm modal state.
   * @param {ConfirmModalState | null} state Modal request to render.
   */
  onHostStateChange = (state) => {
    this.s.state = state
  }

  /** Resolves the active confirmation as cancelled. */
  onCancelPress = () => {
    this.s.state?.resolve(false)
  }

  /** Resolves the active confirmation as confirmed. */
  onConfirmPress = () => {
    this.s.state?.resolve(true)
  }
}

/** @type {NamedExoticComponent<ConfirmModalHostProps>} */
const ConfirmModalHostComponent = memo(shapeComponent(ConfirmModalHost))

export default ConfirmModalHostComponent
